import {
  type ComplexLoginMultiFormData,
  CredentialType,
} from '@/sections/asset/components/tabs/credentials/credentialForms';
import type {
  RowMode,
  WizardCredentialData,
} from '@/sections/seeds/components/modals/AddSeedByTypeModal/AddSeedByTypeModal.types';

/**
 * Splits the seed textarea into trimmed, non-empty lines — the CANONICAL seed
 * split shared by seed CREATION (`useAddSeedSubmit`) and credential ASSIGNMENT
 * (`CredentialStep`, `useConfigureCredential`, `CredentialAssignStep`, the
 * `firstSeedUrl` derivation in `index.tsx`). All five sites MUST call this one
 * implementation or credential assignment desyncs from the seeds actually
 * created.
 *
 * Order-preserving dedupe (`[...new Set(...)]`) is intentional: duplicate URLs
 * pasted into the textarea would otherwise (a) collide as duplicate React keys
 * and share Radix accordion state in the credential-assign UI, and (b) produce N
 * identical seed PUTs for one target. Deduping collapses N duplicate lines to a
 * single seed — the server treats the PUTs idempotently, so this is the intended
 * semantic (only the duplicate-key/desync bug is fixed; unique input is
 * unaffected).
 */
export function parseSeedLines(textInput: string): string[] {
  return [
    ...new Set(
      textInput
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)
    ),
  ];
}

/** Counts the non-empty (trimmed) lines in the seed textarea input. */
export function parseSeedCount(textInput: string): number {
  return parseSeedLines(textInput).length;
}

/**
 * Templates a COMPLEX_LOGIN config onto ONE app: clones every identity with a
 * fresh id (recorderResults keys on id, so per-app clones must not collide) and
 * rewrites loginUrl to the target app's own URL. Does not mutate the source.
 */
export function templateComplexForApp(
  multi: ComplexLoginMultiFormData,
  appUrl: string
): ComplexLoginMultiFormData {
  return {
    hint: multi.hint,
    identities: multi.identities.map(idn => ({
      ...idn,
      id: crypto.randomUUID(),
      loginUrl: appUrl,
    })),
  };
}

/**
 * Resolves the WizardCredentialData to attach to ONE app, or null (= seed
 * created with no credential). 'off' → null; 'shared'/'custom' pick the base
 * bundle (null → null). Only a SHARED COMPLEX_LOGIN base is cloned with its
 * loginUrl templated to appUrl (one bundle fanned across N apps). A CUSTOM
 * COMPLEX_LOGIN base is already app-specific — its identities may carry
 * operator-edited per-identity loginUrls — so it passes through verbatim, as
 * does every other type (static + Custom RECIPE — Option B), with NO templating.
 */
export function resolveAppCredential(
  mode: RowMode,
  shared: WizardCredentialData | null,
  custom: WizardCredentialData | undefined,
  appUrl: string
): WizardCredentialData | null {
  if (mode === 'off') return null;
  const base = mode === 'shared' ? shared : custom;
  if (!base) return null;
  // Only the SHARED bundle is a template fanned across N apps — rewrite each
  // clone's loginUrl to its target app. A CUSTOM bundle is already app-specific
  // (its identities were seeded with this app's URL and may have been edited to
  // per-identity subpaths/hosts), so pass it through verbatim; templating it
  // would silently discard those edits.
  if (mode === 'shared' && base.selectedType === CredentialType.COMPLEX_LOGIN) {
    return {
      ...base,
      complexLogin: templateComplexForApp(base.complexLogin, appUrl),
    };
  }
  return base;
}

/** Counts the apps whose resolved credential config is COMPLEX_LOGIN. */
export function recorderAppCount(
  perApp: Record<string, RowMode>,
  shared: WizardCredentialData | null,
  custom: Record<string, WizardCredentialData>,
  appUrls: string[]
): number {
  return appUrls.filter(url => {
    const resolved = resolveAppCredential(
      perApp[url] ?? 'off',
      shared,
      custom[url],
      url
    );
    return resolved?.selectedType === CredentialType.COMPLEX_LOGIN;
  }).length;
}
